// RC-1 Commercial QA — Data Integrity Fix · Numeric Wheel Guard
// -----------------------------------------------------------------------------
// PRESENTATION / INPUT-BEHAVIOUR ONLY.  NOT ENGINEERING.
//
// HTML `<input type="number">` responds to mousewheel / touchpad wheel events
// by incrementing or decrementing its value.  Operators scrolling an editor
// page can silently modify Hole Size, MW, TVD, MD, Casing OD/ID, etc.  This
// is a data-integrity risk and is treated as a commercial release blocker.
//
// Fix strategy: a single document-level capture-phase `wheel` listener.  When
// a wheel event targets a focused numeric input, we `.blur()` the input
// synchronously — the browser then routes the wheel to page scroll and does
// NOT change the input's value.  Page scrolling remains fully functional
// because we never call preventDefault().
//
// Explicitly does NOT touch:  engineering calculations, RCE, storage,
// navigation, reports, print, auto-population, or any schema.
//
// Reversible in one line by removing the `installNumericWheelGuard()` call.
// -----------------------------------------------------------------------------

let _installed = false;

function _isNumericInput(el) {
  if (!el || el.tagName !== "INPUT") return false;
  // `input.type` is normalised by the DOM ("number" | "text" | …). We match
  // the exact `type="number"` class of inputs the browser increments on
  // wheel; text/textarea/select are untouched.
  return el.type === "number";
}

function _onWheelCapture(e) {
  // Only act if the wheel is landing on a focused numeric input.  We compare
  // against `document.activeElement` so page scroll over an unfocused number
  // input (rare — but possible on desktop trackpads) is a no-op.  The blur
  // takes effect synchronously so the browser skips the value increment.
  const target = e.target;
  const active = document.activeElement;
  if (_isNumericInput(target) && target === active) {
    target.blur();
  }
}

/**
 * Install the global numeric-wheel guard.  Idempotent — safe to call from
 * multiple mount points.  Returns an uninstaller for tests / hot-reload.
 */
export function installNumericWheelGuard() {
  if (_installed) return () => {};
  _installed = true;
  // `capture: true` so we see the event before it dispatches to the input
  // (and thus before the browser applies its default increment).  We keep
  // `passive: true` — we never preventDefault; page scroll must continue.
  document.addEventListener("wheel", _onWheelCapture, { capture: true, passive: true });
  return () => {
    document.removeEventListener("wheel", _onWheelCapture, { capture: true });
    _installed = false;
  };
}
